/**
 * Standard Template Preview Component
 * Renders the template structure from JSON (read-only preview with realistic data)
 */

import { RealisticContainerRenderer } from "./realistic-container-renderer";
import { TemplateProvider } from "./template-context";
import type { ContainerElement } from "@/features/invoice-template/types/template.types";

interface CompanyDetails {
  name?: string;
  street?: string;
  houseNumber?: string;
  zip?: string;
  city?: string;
  country?: string;
  email?: string;
  phone?: string;
  website?: string;
  vatId?: string;
  taxNumber?: string;
  iban?: string;
  bic?: string;
  bankName?: string;
  court?: string;
  hrn?: string;
  contactFirstName?: string;
  contactLastName?: string;
  contactEmail?: string;
}

interface TemplatePreviewProps {
  template: {
    name: string;
    fontFamily?: string;
    fontSize?: number;
    fontColor?: string;
    accentColor?: string;
    logoUrl?: string | null;
    structure: ContainerElement[];
    headerStructure?: ContainerElement;
    footerStructure?: ContainerElement;
    leftMargin?: number;
    rightMargin?: number;
    topMargin?: number;
    bottomMargin?: number;
  };
  previewFontSize?: number;
  previewAccentColor?: string;
  previewLogoUrl?: string | null;
  logoWidth?: number;
  logoHeight?: number;
  companyDetails?: CompanyDetails;
  tableColumns?: {
    description?: boolean;
    productType?: boolean;
    itemDate?: boolean;
    unit?: boolean;
    quantity?: boolean;
    unitPrice?: boolean;
    total?: boolean;
  };
}

export function TemplatePreview({
  template,
  previewFontSize,
  previewAccentColor,
  previewLogoUrl,
  logoWidth,
  logoHeight,
  tableColumns,
  companyDetails,
}: TemplatePreviewProps) {
  const fontFamily = template.fontFamily || "Helvetica";
  const fontSize = previewFontSize ?? template.fontSize ?? 12;
  // Normal text color should always be black, not the accent color
  const fontColor = "#000000";
  // Accent color is stored in rootContainer and used only for specific elements
  const rootContainer = (template as any).rootContainer || {};
  const accentColor = previewAccentColor ?? rootContainer.accentColor ?? template.accentColor ?? "#000000";

  // Get root containers from structure
  const rootContainers = Array.isArray(template.structure) ? template.structure : [];
  const header = template.headerStructure;
  const footer = template.footerStructure;
  
  // Convert pixel margins to mm for display (96 DPI: 1mm ≈ 3.7795px)
  const PX_TO_MM = 1 / 3.7795;
  const leftMarginPx = template.leftMargin ?? 56.7;
  const rightMarginPx = template.rightMargin ?? 56.7;
  const topMarginPx = template.topMargin ?? 56.7;
  const bottomMarginPx = template.bottomMargin ?? 56.7;
  
  // Convert to mm for CSS
  const leftMarginMm = leftMarginPx * PX_TO_MM;
  const rightMarginMm = rightMarginPx * PX_TO_MM;
  const topMarginMm = topMarginPx * PX_TO_MM;
  const bottomMarginMm = bottomMarginPx * PX_TO_MM;

  console.log('TemplatePreview - logoUrl:', previewLogoUrl);

  return (
    <TemplateProvider value={{ tableColumns, companyDetails, logoUrl: previewLogoUrl, logoWidth, logoHeight, accentColor }}>
      <div
        className="bg-white shadow-2xl standard-template-preview relative"
        style={{
          width: "210mm",
          minHeight: "297mm",
          fontFamily,
          fontSize: `${fontSize}px`,
          color: fontColor,
          // Disable all pointer events for non-interactive preview
          pointerEvents: "none",
          userSelect: "none",
        }}
      >
      <style>{`
        .standard-template-preview .border {
          border: none !important;
        }
        .standard-template-preview .rounded {
          border-radius: 0 !important;
        }
        .standard-template-preview .hover\\:border-primary:hover {
          border-color: transparent !important;
        }
        .standard-template-preview .ring-2 {
          box-shadow: none !important;
        }
        .standard-template-preview .bg-primary\\/10 {
          background-color: transparent !important;
        }
      `}</style>

      {/* Header: rendered in top margin area (from 0 to topMargin) */}
      {header && header.visible !== false && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: `${topMarginMm}mm`,
            paddingLeft: `${leftMarginMm}mm`,
            paddingRight: `${rightMarginMm}mm`,
          }}
        >
          <RealisticContainerRenderer
            container={header}
            fontFamily={fontFamily}
            fontSize={fontSize}
            fontColor={fontColor}
          />
        </div>
      )}

      {/* Main content area: between top and bottom margins */}
      <div
        style={{
          marginTop: `${topMarginMm}mm`,
          marginBottom: `${bottomMarginMm}mm`,
          paddingLeft: `${leftMarginMm}mm`,
          paddingRight: `${rightMarginMm}mm`,
          minHeight: `calc(297mm - ${topMarginMm}mm - ${bottomMarginMm}mm)`,
        }}
      >
        {rootContainers.map((container) => (
          <div key={container.id} className="mb-4">
            <RealisticContainerRenderer
              container={container}
              fontFamily={fontFamily}
              fontSize={fontSize}
              fontColor={fontColor}
            />
          </div>
        ))}
      </div>

      {/* Footer: rendered in bottom margin area (from (297mm - bottomMargin) to 297mm) */}
      {footer && footer.visible !== false && (
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: `${bottomMarginMm}mm`,
            paddingLeft: `${leftMarginMm}mm`,
            paddingRight: `${rightMarginMm}mm`,
          }}
        >
          <RealisticContainerRenderer
            container={footer}
            fontFamily={fontFamily}
            fontSize={fontSize}
            fontColor={fontColor}
          />
        </div>
      )}
    </div>
    </TemplateProvider>
  );
}
